import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutocompleteProps } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { StyleService } from '../../services/style.service';
import { AuthService } from '../../services/auth.service';
import { IStyle } from '../../interfaces/garments/style.interface';
import SearchableSelect from '../common/SearchableSelect';

/** How many styles to preload when the box is opened with an empty search. */
export const STYLE_DEFAULT_PAGE_SIZE = 10;
/** How many matches to fetch once the user has typed something. */
export const STYLE_SEARCH_PAGE_SIZE = 20;

const SEARCH_DEBOUNCE_MS = 300;

export const styleLabel = (option: IStyle): string => `${option.styleNumber} — ${option.name}`;

interface UseStyleSearchOptions {
    /** Set false to stop fetching (e.g. the picker is disabled). Defaults to true. */
    enabled?: boolean;
    /** Narrow the search to one buyer's styles — a sample request/order line is always raised
     *  against a specific buyer's style. */
    buyerId?: string;
    /** Only offer active styles — off by default so an existing reference (an already-archived
     *  style on a sample request/order) is never dropped out from under a picker showing it.
     *  L2 fix: forwarded to `StyleService.list`'s server-side `activeOnly` filter (mirrors
     *  `BuyerAutocomplete`'s `activeOnly`) — previously filtered client-side on the fetched page,
     *  which could push active styles off-page on a tenant with many ARCHIVED styles. */
    activeOnly?: boolean;
    onError?: (error: unknown) => void;
}

/**
 * Backs a style picker: preloads the first page of styles so an empty, freshly-focused box
 * already lists options, then debounce-searches the server as the user types. Mirrors
 * `EmployeeAutocomplete.tsx`'s `useEmployeeSearch` — never hand-roll a picker in a screen.
 */
export const useStyleSearch = ({ enabled = true, buyerId, activeOnly = false, onError }: UseStyleSearchOptions = {}) => {
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState<IStyle[]>([]);
    const [loading, setLoading] = useState(false);

    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    useEffect(() => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!enabled || !institutionId) {
            setOptions([]);
            return;
        }
        const search = inputValue.trim();
        let cancelled = false;
        const handle = setTimeout(async () => {
            setLoading(true);
            try {
                const size = search ? STYLE_SEARCH_PAGE_SIZE : STYLE_DEFAULT_PAGE_SIZE;
                const res = await StyleService.list(
                    institutionId, 0, size, buyerId, undefined, search || undefined, activeOnly
                );
                if (!cancelled) setOptions(res.content || []);
            } catch (err) {
                if (!cancelled) {
                    setOptions([]);
                    onErrorRef.current?.(err);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, search ? SEARCH_DEBOUNCE_MS : 0);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [inputValue, enabled, buyerId, activeOnly]);

    const handleInputChange = useCallback(
        (_event: React.SyntheticEvent, value: string, reason: string) => {
            if (reason === 'input') setInputValue(value);
            else if (reason === 'clear') setInputValue('');
        },
        []
    );

    return { options, loading, inputValue, setInputValue, handleInputChange };
};

/** Keeps the selected value present in the option list so MUI can match it. */
const withSelected = (options: IStyle[], selected: IStyle | null): IStyle[] => {
    if (!selected) return options;
    return options.some(o => o.id === selected.id) ? options : [selected, ...options];
};

type PassthroughProps = Partial<Pick<AutocompleteProps<IStyle, false, false, false>, 'openOnFocus' | 'autoHighlight'>>;

interface StyleAutocompleteProps extends PassthroughProps {
    value: IStyle | null;
    onChange: (value: IStyle | null) => void;
    label?: string;
    placeholder?: string;
    size?: 'small' | 'medium';
    fullWidth?: boolean;
    disabled?: boolean;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    sx?: SxProps<Theme>;
    /** Narrow to one buyer's styles. */
    buyerId?: string;
    /** Only offer active styles — off by default, see `useStyleSearch`'s doc. */
    activeOnly?: boolean;
    onError?: (error: unknown) => void;
    'data-testid'?: string;
}

/** Style picker used across the merchandising screens (Sample Request/Buyer Order line forms). */
export function StyleAutocomplete({
    value,
    onChange,
    label = 'Style',
    placeholder = 'Search styles...',
    size = 'small',
    fullWidth = true,
    disabled = false,
    required,
    error,
    helperText,
    sx,
    buyerId,
    activeOnly,
    onError,
    ...rest
}: StyleAutocompleteProps) {
    const { options, loading, inputValue, setInputValue } = useStyleSearch({
        enabled: !disabled,
        buyerId,
        activeOnly,
        onError,
    });

    const mergedOptions = useMemo(() => withSelected(options, value), [options, value]);

    return (
        <SearchableSelect<IStyle>
            options={mergedOptions}
            value={value ? value.id : ''}
            selectedOption={value}
            onChange={(_id, option) => onChange(option)}
            getOptionLabel={styleLabel}
            // The server has already filtered; re-filtering here would hide matches.
            disableClientFilter
            onInputChange={setInputValue}
            loading={loading}
            disabled={disabled}
            fullWidth={fullWidth}
            size={size}
            sx={sx}
            label={label}
            placeholder={placeholder}
            required={required}
            error={error}
            helperText={helperText}
            noOptionsText={loading ? 'Loading…' : inputValue ? 'No styles found' : 'No styles'}
            {...rest}
        />
    );
}

export default StyleAutocomplete;
